import { PASSWORD_MIN_LENGTH } from '@hilo/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { api } from '../../lib/api';
import { errorText } from '../../lib/copy';
import { forgetSession, logout, useCurrentUser } from '../../lib/session';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { IconLogout } from '../../ui/icons';
import styles from './AuthForm.module.css';
import { AuthLayout } from './AuthLayout';
import { setLoginNotice } from './notice';
import { PasswordField } from './PasswordField';

export function ChangePasswordPage() {
  const client = useQueryClient();
  const user = useCurrentUser();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [localError, setLocalError] = useState<{ field: 'current' | 'next' | 'repeat'; text: string } | null>(null);
  const mutation = useMutation({
    mutationFn: () => api<void>('POST', '/auth/change-password', { body: { currentPassword: current, newPassword: next } }),
    // El servidor revoca la sesión al cambiar la contraseña: hay que volver a entrar.
    onSuccess: () => {
      setLoginNotice('Contraseña cambiada. Entra con la nueva.');
      forgetSession(client);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!current) return setLocalError({ field: 'current', text: 'Escribe la contraseña temporal que te dieron.' });
    if (next.length < PASSWORD_MIN_LENGTH) {
      return setLocalError({ field: 'next', text: `La contraseña nueva necesita al menos ${PASSWORD_MIN_LENGTH} caracteres.` });
    }
    if (next !== repeat) return setLocalError({ field: 'repeat', text: 'Las dos contraseñas nuevas no coinciden.' });
    setLocalError(null);
    if (!mutation.isPending) mutation.mutate();
  }

  const error = localError?.text ?? (mutation.isError ? errorText(mutation.error) : null);

  return (
    <AuthLayout>
      <p className={styles.eyebrow}>Primer acceso</p>
      <h1 className={styles.title}>Crea tu contraseña</h1>
      <p className={styles.lead}>
        {user.displayName}, entraste con una contraseña temporal. Elige una propia para seguir; después volverás a entrar con ella.
      </p>
      <form className={styles.form} onSubmit={submit} noValidate>
        <PasswordField label="Contraseña temporal" autoComplete="current-password" value={current} onChange={setCurrent}
          invalid={localError?.field === 'current'} />
        <PasswordField label="Contraseña nueva" autoComplete="new-password" value={next} onChange={setNext}
          hint={`Al menos ${PASSWORD_MIN_LENGTH} caracteres. Una frase de varias palabras es fácil de recordar y difícil de adivinar.`}
          invalid={localError?.field === 'next'} />
        <PasswordField label="Repite la contraseña nueva" autoComplete="new-password" value={repeat} onChange={setRepeat}
          invalid={localError?.field === 'repeat'} />
        {error && <p className={styles.error} role="alert">{error}</p>}
        <button className={`btn btn-primary ${styles.submit}`} type="submit" disabled={mutation.isPending} aria-busy={mutation.isPending}>
          {mutation.isPending ? <><span className={styles.spinner} aria-hidden="true" />Guardando…</> : 'Cambiar contraseña'}
        </button>
        <button className={`btn btn-quiet ${styles.secondary}`} type="button" onClick={() => setConfirmingLogout(true)}>Cerrar sesión</button>
      </form>
      <ConfirmDialog open={confirmingLogout} icon={<IconLogout />} title="¿Cerrar sesión?"
        confirmLabel="Cerrar sesión" busyLabel="Cerrando…" busy={leaving}
        onConfirm={() => { setLeaving(true); void logout(client); }} onCancel={() => setConfirmingLogout(false)}>
        <p>Todavía no has creado tu contraseña. Para volver a entrar usarás la contraseña temporal, que caduca a las 24 horas de crearse.</p>
      </ConfirmDialog>
    </AuthLayout>
  );
}
